import React, { FC } from 'react';
import {
    SkeletonBlock,
    SkeletonFooterBar,
    SkeletonPageHeader,
    SkeletonPageShell,
    SkeletonResponsiveGrid,
    SkeletonRow,
    SkeletonStack,
    SkeletonSurface,
    SkeletonTable,
    SkeletonTableRow,
    SkeletonTabs
} from 'src/components/widgets/skeleton';

const RulesTableSkeleton: FC = () => (
    <SkeletonTable>
        {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonTableRow key={index} $columns='minmax(180px, 1fr) 76px'>
                <SkeletonBlock $width={index % 2 === 0 ? '220px' : '160px'} $height='16px' />
                <SkeletonRow $gap='8px' $justify='flex-end'>
                    <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                    <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                </SkeletonRow>
            </SkeletonTableRow>
        ))}
    </SkeletonTable>
);

const PresetGridSkeleton: FC<{ rows?: number }> = ({ rows = 1 }) => (
    <SkeletonResponsiveGrid
        $columns='repeat(4, minmax(0, 1fr))'
        $gap='12px'
        $tabletColumns='repeat(3, minmax(0, 1fr))'
        $mobileColumns='repeat(2, minmax(0, 1fr))'
    >
        {Array.from({ length: rows * 4 }).map((_, index) => (
            <SkeletonSurface key={index} $padding='10px'>
                <SkeletonStack $gap='10px'>
                    <SkeletonBlock $height='82px' $radius='6px' />
                    <SkeletonBlock $width={index % 2 === 0 ? '70%' : '55%'} $height='13px' />
                </SkeletonStack>
            </SkeletonSurface>
        ))}
    </SkeletonResponsiveGrid>
);

const TogglePresetSectionSkeleton: FC<{ rows?: number }> = ({ rows = 1 }) => (
    <SkeletonStack $gap='14px'>
        <SkeletonRow $gap='12px'>
            <SkeletonBlock $width='42px' $height='22px' $radius='999px' />
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='160px' $height='18px' />
                <SkeletonBlock $width='min(520px, 100%)' $height='12px' />
            </SkeletonStack>
        </SkeletonRow>
        <PresetGridSkeleton rows={rows} />
    </SkeletonStack>
);

export const AIVirtualModelRulesPageSkeleton: FC = () => (
    <SkeletonPageShell aria-hidden='true' $padding='20px 0 92px'>
        <SkeletonPageHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonRow $gap='10px'>
                    <SkeletonBlock $width='140px' $height='28px' />
                    <SkeletonBlock $width='82px' $height='22px' $radius='999px' />
                    <SkeletonBlock $width='64px' $height='22px' $radius='999px' />
                </SkeletonRow>
                <SkeletonBlock $width='min(620px, 100%)' $height='13px' />
                <SkeletonBlock $width='90px' $height='13px' />
            </SkeletonStack>
            <SkeletonBlock $width='88px' $height='36px' $radius='6px' />
        </SkeletonPageHeader>
        <SkeletonTabs tabWidths={['52px', '112px']} />
        <RulesTableSkeleton />
    </SkeletonPageShell>
);

export const AIVirtualModelRulePageSkeleton: FC = () => (
    <SkeletonPageShell aria-hidden='true' $padding='20px 0 92px'>
        <SkeletonPageHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonRow $gap='10px'>
                    <SkeletonBlock $width='140px' $height='28px' />
                    <SkeletonBlock $width='82px' $height='22px' $radius='999px' />
                    <SkeletonBlock $width='64px' $height='22px' $radius='999px' />
                </SkeletonRow>
                <SkeletonBlock $width='min(620px, 100%)' $height='13px' />
                <SkeletonBlock $width='120px' $height='13px' />
            </SkeletonStack>
        </SkeletonPageHeader>
        <SkeletonStack $gap='20px'>
            <SkeletonSurface $padding='22px'>
                <SkeletonStack $gap='10px'>
                    <SkeletonBlock $width='54px' $height='13px' />
                    <SkeletonBlock $height='38px' $radius='6px' />
                </SkeletonStack>
            </SkeletonSurface>
            <SkeletonSurface $padding='22px'>
                <SkeletonStack $gap='22px'>
                    <SkeletonBlock $width='88px' $height='20px' />
                    <SkeletonStack $gap='10px'>
                        <SkeletonBlock $width='140px' $height='13px' />
                        <SkeletonBlock $width='min(460px, 100%)' $height='12px' />
                        <SkeletonBlock $width='140px' $height='38px' $radius='6px' />
                    </SkeletonStack>
                    <SkeletonStack $gap='14px'>
                        <SkeletonRow $gap='12px'>
                            <SkeletonBlock $width='170px' $height='18px' />
                            <SkeletonBlock $width='42px' $height='22px' $radius='999px' />
                        </SkeletonRow>
                        <SkeletonBlock $width='min(560px, 100%)' $height='12px' />
                        <PresetGridSkeleton rows={2} />
                    </SkeletonStack>
                    <TogglePresetSectionSkeleton rows={2} />
                    <TogglePresetSectionSkeleton />
                    <TogglePresetSectionSkeleton />
                    <TogglePresetSectionSkeleton />
                </SkeletonStack>
            </SkeletonSurface>
            <SkeletonBlock $height='58px' $radius='6px' />
            <SkeletonBlock $height='58px' $radius='6px' />
        </SkeletonStack>
        <SkeletonFooterBar>
            <SkeletonBlock $width='108px' $height='36px' $radius='6px' />
            <SkeletonBlock $width='88px' $height='36px' $radius='6px' />
        </SkeletonFooterBar>
    </SkeletonPageShell>
);
